import { DirectionalLight, Object3D, Scene as ThreeScene, SpotLight, Vector3, Quaternion } from "three";
import { FrameFeatureBox, PanoFrameFeatureBox } from "../framefeature/internal";
import { GeometryNode } from "../geometries/internal";
import { isDefined, isNull } from "../../internal";
import { PanoCamera, QuadtreeCamera, SceneCamera } from "../cameras/internal";
import { Scene } from "../internal";
import { UserInputHandler } from "../../engine/internal";
/**
 * This is a concrete implementation of the Scene object for using it in an infra3d application. It uses three.js for rendering<br/>
 * <img width="600px" src="../../art/objectrelations.png" alt="" />
 * @class InfraScene
 * @extends Scene
 * @constructor
 */
export class InfraScene extends Scene {
    constructor(engine) {
        super(engine);
        this._lastrendertime = null;
        this._animid = -1;
        this._getSnapShotBeforeGeometryIsRendered = null;
        this.panoramaMode = false;
        this.multiPerspectiveMode = false;
        this.panoZoom = -1;
        this.sceneZoom = -1;
        this.tFeatureNode = null;
        this.tMultiPerspectiveScene = null;
        this.multiperspectiveCamera = null;
        this.tQuadtreeScene = new ThreeScene();
        this.tPanoQuadtreeScene = new ThreeScene();
        this.tPanoFrameFeatureScene = new ThreeScene();
        this.tStereoFrameFeatureScene = new ThreeScene();
        this.tMultiPerspectiveScene = new ThreeScene();
        this.tScene = new ThreeScene();
        this.tFeatureNode = new Object3D();
        this.tScene.add(this.tFeatureNode);
        const light = new DirectionalLight(0xffffff, 0.9);
        light.position.set(1, 1, 1); //this is
        this.tScene.add(light);
        const light2 = new SpotLight(0xffffff, 3);
        light2.position.set(0, 0, 0); //this is
        this.tScene.add(light2);
        // cameras
        this.quadtreeCamera = new QuadtreeCamera(null);
        this.sceneCamera = new SceneCamera(null);
        this.panoCamera = new PanoCamera(this);
        this.multiperspectiveCamera = new PanoCamera(this);
        this.mainCamera = this.sceneCamera;
        this.traversalState.cameras["quadtreeCamera"] = this.quadtreeCamera;
        this.traversalState.cameras["sceneCamera"] = this.sceneCamera;
        this.traversalState.cameras["panoCamera"] = this.panoCamera;
        this.traversalState.cameras["multiperspectiveCamera"] = this.multiperspectiveCamera;
        this.add(this.quadtreeCamera);
        this.add(this.sceneCamera);
        this.add(this.panoCamera);
        this.add(this.multiperspectiveCamera);
        this.userInputHandler = new UserInputHandler(this.engine.renderer.domElement, this.traversalState, this);
        //Private variables
        this._animid = -1;
        this._lastrendertime = Date.now();
        this._previousCameraPosition = null;
        this.render();
    }
    add(node, options = {}) {
        super.add(node, options);
        // add it to the 3d scene
        if (!node.initialized)
            node.init(this.traversalState);
        if (isDefined(node.iMesh) && node.iMesh != null) {
            //check if node is a feature
            if (node instanceof GeometryNode) {
                this.tFeatureNode.add(node.iMesh);
            }
            else {
                this.tScene.add(node.iMesh);
            }
        }
        return true;
    }
    addForFrame(node, options = {}) {
        super.add(node, options);
        // add it to the 3d scene
        if (!node.initialized)
            node.init(this.traversalState);
        if (isDefined(node.iMesh) && node.iMesh != null) {
            if (node instanceof PanoFrameFeatureBox) {
                this.tPanoFrameFeatureScene.add(node.iMesh);
                this.tPanoFrameFeatureScene.add(node.iBorder);
            }
            else if (node instanceof FrameFeatureBox) {
                this.tStereoFrameFeatureScene.add(node.iMesh);
                this.tStereoFrameFeatureScene.add(node.iBorder);
            }
            else {
                this.tQuadtreeScene.add(node.iMesh);
            }
        }
        return true;
    }
    remove(node) {
        try {
            if (node instanceof PanoFrameFeatureBox) {
                this.tPanoFrameFeatureScene.remove(node.iMesh);
                this.tPanoFrameFeatureScene.remove(node.iBorder);
            }
            else if (node instanceof FrameFeatureBox) {
                this.tStereoFrameFeatureScene.remove(node.iMesh);
                this.tStereoFrameFeatureScene.remove(node.iBorder);
            }
            else {
                this.tScene.remove(node.iMesh);
                this.tQuadtreeScene.remove(node.iMesh);
                this.tPanoQuadtreeScene.remove(node.iMesh);
                this.tFeatureNode.remove(node.iMesh);
                this.tMultiPerspectiveScene.remove(node.iMesh);
            }
        }
        catch (e) {
            console.log("ERROR infrascene remove");
        }
        for (let i = 0; i < this.nodes.length; i++) {
            if (this.nodes[i] == node)
                this.nodes.splice(i, 1);
        }
        node.destroy();
    }
    removeForFrame(node) {
        if (node.iMesh != null) {
            if (node instanceof PanoFrameFeatureBox) {
                this.tPanoFrameFeatureScene.remove(node.iMesh);
                this.tPanoFrameFeatureScene.remove(node.iBorder);
            }
            else if (node instanceof FrameFeatureBox) {
                this.tStereoFrameFeatureScene.remove(node.iMesh);
                this.tStereoFrameFeatureScene.remove(node.iBorder);
            }
            else {
                this.tQuadtreeScene.remove(node.iMesh);
            }
        }
        for (let i = 0; i < this.nodes.length; i++) {
            if (this.nodes[i] == node)
                this.nodes.splice(i, 1);
        }
    }
    /**
     * Will be called in every render pass
     *
     * reads:   traversalstate.render
     *          traversalstate.panX
     *          traversalstate.panY
     *          traversalstate.lockrendering
     *
     * writes:  traversalstate.quadtreeCamera
     *
     *
     * @method render
     */
    render() {
        super.render();
        this.engine.renderer.clear();
        const time = Date.now();
        this.traversalState.t = time - this._lastrendertime;
        this._lastrendertime = time;
        this.traversalState.lockRendering = false;
        if (this.panoramaMode) {
            this.panoCamera.behaviors[0].disable = false;
            this.multiperspectiveCamera.behaviors[0].disable = true;
            this.quadtreeCamera.behaviors[0].disable = true;
            this.sceneCamera.behaviors[0].disable = true;
        }
        else if (this.multiPerspectiveMode) {
            this.panoCamera.behaviors[0].disable = true;
            this.multiperspectiveCamera.behaviors[0].disable = false;
            this.quadtreeCamera.behaviors[0].disable = true;
            this.sceneCamera.behaviors[0].disable = true;
        }
        else {
            this.panoCamera.behaviors[0].disable = true;
            this.multiperspectiveCamera.behaviors[0].disable = true;
            this.quadtreeCamera.behaviors[0].disable = false;
            this.sceneCamera.behaviors[0].disable = false;
        }
        for (let i = 0; i < this.nodes.length; i++) {
            this.nodes[i].render(this.traversalState);
        }
        // QUADTREE RENDER PASS
        if (this.panoramaMode) {
            this.engine.renderer.render(this.tPanoQuadtreeScene, this.panoCamera.iCamera);
            this.engine.renderer.render(this.tPanoFrameFeatureScene, this.panoCamera.iCamera);
        }
        else if (this.multiPerspectiveMode) {
            this.engine.renderer.render(this.tMultiPerspectiveScene, this.multiperspectiveCamera.iCamera);
            this.engine.renderer.render(this.tPanoFrameFeatureScene, this.multiperspectiveCamera.iCamera);
        }
        else {
            this.engine.renderer.render(this.tQuadtreeScene, this.quadtreeCamera.iCamera);
            this.engine.renderer.render(this.tStereoFrameFeatureScene, this.quadtreeCamera.iCamera);
        }
        // Screenshot capture before geometry is rendered
        if (!isNull(this._getSnapShotBeforeGeometryIsRendered)) {
            const imgbuf = this.engine.getImageBuffer(this._ss_x, this._ss_y, this._ss_w, this._ss_h);
            this._getSnapShotBeforeGeometryIsRendered(imgbuf);
            this._getSnapShotBeforeGeometryIsRendered = null;
        }
        // GEOMETRY RENDER PASS
        const pos = new Vector3();
        const quat = new Quaternion();
        const scale = new Vector3();
        this.traversalState.matModelView.decompose(pos, quat, scale);
        this.tScene.position.copy(pos);
        this.tScene.setRotationFromQuaternion(quat);
        this.tScene.scale.copy(scale);
        if (this.panoramaMode) {
            this.engine.renderer.render(this.tScene, this.panoCamera.iCamera);
            this.traversalState.actualCamera = this.panoCamera;
            this.traversalState.actualSceneCamera = this.panoCamera;
        }
        else if (this.multiPerspectiveMode) {
            this.engine.renderer.render(this.tScene, this.multiperspectiveCamera.iCamera);
            this.traversalState.actualCamera = this.multiperspectiveCamera;
            this.traversalState.actualSceneCamera = this.multiperspectiveCamera;
        }
        else {
            this.engine.renderer.render(this.tScene, this.sceneCamera.iCamera);
            this.traversalState.actualCamera = this.quadtreeCamera;
            this.traversalState.actualSceneCamera = this.sceneCamera;
        }
        this._notifyCameraChange(this.traversalState.actualSceneCamera);
        // Zoom handling
        this.panoZoom = this.traversalState.panoZoom;
        if (this.onPanoZoom != null && this.panoZoom != -1) {
            this.onPanoZoom(this.traversalState.panoZoom);
        }
        this.sceneZoom = this.traversalState.zoom;
        if (this.onSceneZoom != null) {
            this.onSceneZoom(this.traversalState.zoom);
        }
        this.traversalState.resetUserEvents();
        if (this._animid < 0) {
            this.traversalState.lockRendering = true;
            const animate = () => {
                this._animid = requestAnimationFrame(animate);
                if (this.traversalState.lockRendering || this.renderContinuously) {
                    this.render();
                }
            };
            animate();
        }
    }
    lockNavigation() {
        this.traversalState.lockNavigation = true;
    }
    unlockNavigation() {
        this.traversalState.lockNavigation = false;
    }
    /**
     * Get screenshot before geometry is rendered
     * @method getSnapShotAsync
     * @param {number} x
     * @param {number} y
     * @param {number} w
     * @param {number}  h
     * @param cbf
     */
    getSnapShotAsync(x, y, w, h, cbf) {
        this._ss_x = x;
        this._ss_y = y;
        this._ss_w = w;
        this._ss_h = h;
        this._getSnapShotBeforeGeometryIsRendered = cbf;
    }
    /**
     * Reset screenshot before geometry is rendered
     * @method resetSnapShotAsync
     */
    resetSnapShotAsync() {
        this._getSnapShotBeforeGeometryIsRendered = null;
    }
    /**
     * set the gamma value for image-tiles
     * @param {number} gammavalue [0.2 ... 3]
     */
    setGamma(gammavalue) {
        this._gamma = Math.max(Math.min(gammavalue, 4), 0.2);
        this.traversalState.gamma = this._gamma;
    }
    getGamma() {
        return this._gamma;
    }
    /**
     * Set distance from ground to camera -> camera height
     * @method setCamToGroundDistance
     * @param {Plane} groundplane
     */
    setGroundPlane(groundplane) {
        this.traversalState.groundPlane = groundplane;
        this.traversalState.groundPlaneUpdated = true;
        this.render();
    }
    hideAllFeatures() {
        this.tFeatureNode.visible = false;
    }
    showAllFeatures() {
        this.tFeatureNode.visible = true;
    }
    destroy() {
        this.render = () => {
            return;
        };
        this.tQuadtreeScene = null;
        this.tPanoQuadtreeScene = null;
        this.tPanoFrameFeatureScene = null;
        this.tStereoFrameFeatureScene = null;
        this.tMultiPerspectiveScene = null;
        this.tScene = null;
        this.tFeatureNode = null;
        // cameras
        this.quadtreeCamera = null;
        this.sceneCamera = null;
        this.panoCamera = null;
        this.multiperspectiveCamera = null;
        this.userInputHandler = null;
    }
    _notifyCameraChange(camera) {
        if (typeof this.onCameraMoved === "function") {
            if (this._previousCameraPosition === null ||
                !camera.getPosition().equals(this._previousCameraPosition) ||
                this._previousCameraLatLong === null ||
                camera.getLatLon()[0] !== this._previousCameraLatLong[0] ||
                camera.getLatLon()[1] !== this._previousCameraLatLong[1] ||
                camera.getLatLon()[2] !== this._previousCameraLatLong[2]) {
                this._previousCameraPosition = camera.getPosition();
                this._previousCameraLatLong = camera.getLatLon();
                this.onCameraMoved(camera);
            }
        }
    }
    setCameraView(cameraView, smooth = true) {
        if (this.panoramaMode) {
            if (cameraView.type === "pano") {
                const curLatLonFov = this.panoCamera.getLatLon();
                if (smooth) {
                    this.panoCamera.setLatLon(isNaN(cameraView.lat) ? curLatLonFov[0] : cameraView.lat, isNaN(cameraView.lon) ? curLatLonFov[1] : cameraView.lon, isNaN(cameraView.fov) ? curLatLonFov[2] : cameraView.fov);
                }
                else {
                    this.panoCamera.setInitLatLon(isNaN(cameraView.lat) ? curLatLonFov[0] : cameraView.lat, isNaN(cameraView.lon) ? curLatLonFov[1] : cameraView.lon, isNaN(cameraView.fov) ? curLatLonFov[2] : cameraView.fov);
                }
            }
        }
        else if (this.multiPerspectiveMode) {
            if (cameraView.type === "pano") {
                const curLatLonFov = this.multiperspectiveCamera.getLatLon();
                if (smooth) {
                    this.multiperspectiveCamera.setLatLon(isNaN(cameraView.lat) ? curLatLonFov[0] : cameraView.lat, isNaN(cameraView.lon) ? curLatLonFov[1] : cameraView.lon, isNaN(cameraView.fov) ? curLatLonFov[2] : cameraView.fov);
                }
                else {
                    this.multiperspectiveCamera.setInitLatLon(isNaN(cameraView.lat) ? curLatLonFov[0] : cameraView.lat, isNaN(cameraView.lon) ? curLatLonFov[1] : cameraView.lon, isNaN(cameraView.fov) ? curLatLonFov[2] : cameraView.fov);
                }
            }
        }
        else {
            if (cameraView.type === "panzoom") {
                const curPanZoom = this.quadtreeCamera.getZoomPan();
                this.quadtreeCamera.setZoomPan(isNaN(cameraView.zoom) ? curPanZoom[0] : cameraView.zoom, isNaN(cameraView.panX) ? curPanZoom[1] : cameraView.panX, isNaN(cameraView.panY) ? curPanZoom[2] : cameraView.panY);
            }
        }
        this.render();
    }
    getCameraView() {
        if (this.panoramaMode) {
            const curLatLonFov = this.panoCamera.getLatLon();
            return {
                type: "pano",
                lat: curLatLonFov[0],
                lon: curLatLonFov[1],
                fov: curLatLonFov[2],
            };
        }
        else if (this.multiPerspectiveMode) {
            const curLatLonFov = this.multiperspectiveCamera.getLatLon();
            return {
                type: "pano",
                lat: curLatLonFov[0],
                lon: curLatLonFov[1],
                fov: curLatLonFov[2],
            };
        }
        else {
            const curPanZoom = this.quadtreeCamera.getZoomPan();
            return {
                type: "panzoom",
                zoom: curPanZoom[0],
                panX: curPanZoom[1],
                panY: curPanZoom[2],
            };
        }
    }
}
//# sourceMappingURL=infrascene.js.map